import { TRANSLATIONS } from '@pperform/pp-constants/translations';
import { RootHtmlAttributeKey } from 'constants/globalConfig.constants';
import { Scenario } from 'constants/scenario.constants';
import APIError from 'exceptions/APIError';
import { getGlobalConfigFetcher } from 'fetchers/globalConfig.fetchers';
import type { GlobalConfig } from 'interfaces/GlobalConfig.interface';
import type {
  ShipmentParams,
  SubscriptionParams,
} from 'interfaces/Params.interface';
import type {
  AdditionalValidation,
  Configs,
  SearchField,
  SearchFormConfig,
  SearchFormField,
} from 'interfaces/WidgetConfigs.interface';
import { getParameterByName } from 'utils/queryString.utils';
import {
  getAttributeFromElement,
  getParamsFromTag,
  getParamsFromUrl,
  mapPTPConfig,
} from './globalConfig.utils';

interface SearchFormConfigs {
  searchFields: SearchField[];
  searchForm: SearchFormConfig | undefined;
  searchFormFields: SearchFormField[] | undefined;
  additionalValidation: AdditionalValidation | undefined;
}

export function getLocationConsent(): string | undefined {
  // location consent from URL has higher priority
  const urlConsent = getParameterByName('pp_location_consent');
  if (urlConsent) return urlConsent;
  const htmlConsent = getAttributeFromElement('data-location-consent');
  return htmlConsent;
}

export const getIsDemoAccount = (): boolean => {
  const urlParam = getParameterByName('pp_is_demo_account');
  if (urlParam) {
    return urlParam === 'true';
  }

  return (
    getAttributeFromElement(RootHtmlAttributeKey.DataIsDemoAccount) === 'true'
  );
};

/**
 * The v2 demo gate. Attribute only — deliberately NOT mirroring the
 * `pp_is_demo_account` URL parameter that `getIsDemoAccount` honours. The
 * attribute is written by demo generation; a URL parameter is written by
 * whoever holds the link, so mirroring it would let anyone point a live
 * merchant's tracking page at a bridge that is not there.
 *
 * Separate from `getIsDemoAccount` on purpose. That signal is live and carries
 * v1 behaviour — session telemetry and the returns-label synthetic events — so
 * a v2 demo document must never set `data-is-demo-account`.
 */
export const getIsDemoExperience = (): boolean =>
  getAttributeFromElement(RootHtmlAttributeKey.DataIsDemoExperience) === 'true';

export const getLanguageToUse = (globalConfig: GlobalConfig): string => {
  const dynamicLanguageParam = globalConfig.config?.dynamicLanguageURLParam
    ?.isEnabled
    ? getParameterByName(globalConfig.config?.dynamicLanguageURLParam?.urlParam)
    : null;

  // If the language from URL param is provided but not supported, fallback to `en`
  if (dynamicLanguageParam) {
    return TRANSLATIONS[dynamicLanguageParam] ? dynamicLanguageParam : 'en';
  }

  // Otherwise, use the language from config
  return globalConfig.config?.language || 'en';
};

export const getShipmentParams = (configs: Configs): ShipmentParams[] => {
  let searchFields = [...configs.searchingIdReferencesBox];

  if (configs.return?.searchFields) {
    searchFields = [...searchFields, ...configs.return.searchFields];
  }

  const paramsFromUrl = getParamsFromUrl(searchFields);

  if (paramsFromUrl.length) {
    return paramsFromUrl;
  }

  return getParamsFromTag(searchFields);
};

export const getSubscriptionParams = (): SubscriptionParams => {
  const isUnsubscribe = getParameterByName('pp_unsubscribe') === 'true';
  const mediumType = getParameterByName('medium_type');
  const handler = getParameterByName('handler');

  return { isUnsubscribe, mediumType, handler };
};

export const getScenarioParams = (): Scenario => {
  const returnFlag =
    getParameterByName('pp_return') || getAttributeFromElement('data-return');

  if (returnFlag && returnFlag?.toLowerCase() === 'true') {
    return Scenario.Return;
  }

  return Scenario.Forward;
};

export const getSearchFormConfigs = (configs: Configs): SearchFormConfigs => {
  const scenario = getScenarioParams();

  let searchFields = [...configs.searchingIdReferencesBox];
  if (configs.return?.searchFields) {
    searchFields = [...searchFields, ...configs.return.searchFields];
  }

  const configMaps = {
    [Scenario.Forward]: {
      searchFields,
      searchForm: configs.searchForm,
      searchFormFields: configs.searchFormFieldsBox,
      additionalValidation: configs.additionalIdReferencesBox,
    },
    [Scenario.Return]: {
      searchFields,
      searchForm: configs.return.searchForm,
      searchFormFields: configs.return.searchFormFields,
      additionalValidation: configs.return.additionalValidation,
    },
  };

  return configMaps[scenario];
};

export const getGlobalConfig = async (
  cmsSlug: string,
): Promise<GlobalConfig> => {
  try {
    const data = await getGlobalConfigFetcher(cmsSlug);

    if ('redirect' in data) {
      throw new APIError('slug not found');
    }

    return mapPTPConfig(data);
  } catch (error) {
    throw new APIError(error.message);
  }
};
